import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banknote, Check, ChevronDown, CreditCard, Landmark, Minus, MoreHorizontal,
  Plus, Printer, Search, Trash2, UserPlus, X,
} from "lucide-react";
import {
  PRICING_MODE_DEFINITIONS,
  computeDocumentTax,
  formatRatePercent,
  type PricingMode,
  type TaxLineInput,
} from "@laundrypro/shared";
import {
  Button,
  Card,
  ErrorText,
  Input,
  Modal,
  PageHeader,
  Skeleton,
  useToast,
} from "../components/ui";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";
import { currencyLabel, money } from "../lib/format";

interface CustomerHit {
  id: string;
  name: string;
  mobile: string | null;
  outstandingMinor: number;
}

interface CatalogItemType {
  id: string;
  nameAr: string;
  nameEn: string;
}

interface CatalogService {
  id: string;
  nameAr: string;
  nameEn: string;
  pricingMode: PricingMode;
}

interface PriceEntry {
  serviceId: string;
  itemTypeId: string | null;
  unitPriceMinor: number;
}

interface TaxInfo {
  rateBps: number;
  pricesInclusive: boolean;
  vatEnabled: boolean;
}

/** One item+service combination on the order. Quantity is a number, not a
 *  form string: the only way to change it is ±, so it can never be mid-edit. */
interface OrderLine {
  itemTypeId: string;
  serviceId: string;
  quantity: number;
}

const lineKey = (itemTypeId: string, serviceId: string) => `${itemTypeId}::${serviceId}`;

type PaymentMethod = "CASH" | "CARD" | "TRANSFER" | "OTHER";

const PAYMENT_METHODS: ReadonlyArray<{
  value: PaymentMethod;
  labelAr: string;
  labelEn: string;
  icon: typeof Banknote;
}> = [
  { value: "CASH", labelAr: "نقداً", labelEn: "Cash", icon: Banknote },
  { value: "CARD", labelAr: "بطاقة", labelEn: "Card", icon: CreditCard },
  { value: "TRANSFER", labelAr: "تحويل", labelEn: "Transfer", icon: Landmark },
  { value: "OTHER", labelAr: "أخرى", labelEn: "Other", icon: MoreHorizontal },
];

/** What the server returns from a completed direct sale. */
interface DirectSaleResult {
  orderRef: string;
  invoiceId: string;
  invoiceRef: string;
  totalMinor: number;
  method: PaymentMethod;
}

/**
 * Order creation, built for a laundry counter rather than for a form.
 *
 * ── WHY TILES, NOT SELECTS ──────────────────────────────────────────────────
 *
 * The previous screen was a row of dropdowns per line: open a select, find the
 * item, open another, find the service, type a quantity. That is four
 * interactions and two menu searches for one garment, with the operator's eyes
 * leaving the counter each time. Here an item is one tap and a service is one
 * tap, the price is on the tile before it is chosen, and tapping the same pair
 * again increments it. Nothing about the catalogue is hardcoded — the tiles ARE
 * the owner's item types, services and prices, so deactivating a service
 * removes its tile with no frontend change.
 *
 * ── THE CUSTOMER IS OPTIONAL ────────────────────────────────────────────────
 *
 * A walk-in dropping off two shirts should not be interrogated, and inventing a
 * placeholder "Walk-in" customer per transaction would fill the customer list
 * with rows describing nobody. So the customer section is collapsed by default
 * and the order saves without one (migration 0009 made the column nullable).
 *
 * Totals here are a PREVIEW computed by the same shared tax engine the server
 * uses. The server still re-prices and re-totals everything on save; this is
 * presentation, not authority.
 */
export function OrderCreatePage() {
  const { language } = useBranding();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const ar = language === "ar";
  const currency = currencyLabel(language);

  // ── Catalog ──────────────────────────────────────────────────────────────
  const [itemTypes, setItemTypes] = useState<CatalogItemType[] | null>(null);
  const [services, setServices] = useState<CatalogService[] | null>(null);
  const [prices, setPrices] = useState<PriceEntry[] | null>(null);
  const [frequent, setFrequent] = useState<{ itemTypeId: string; serviceId: string }[]>([]);
  const [tax, setTax] = useState<TaxInfo>({ rateBps: 0, pricesInclusive: true, vatEnabled: false });
  const [catalogError, setCatalogError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [it, sv, pr] = await Promise.all([
          apiFetch<{ items: CatalogItemType[] }>("/item-types"),
          apiFetch<{ items: CatalogService[] }>("/services"),
          apiFetch<{ items: PriceEntry[]; tax: TaxInfo }>("/pricing"),
        ]);
        setItemTypes(it.items);
        setServices(sv.items);
        setPrices(pr.items);
        setTax(pr.tax);
      } catch (err) {
        setCatalogError(
          err instanceof ApiError ? err.message : ar ? "تعذّر تحميل الأسعار." : "Couldn't load the catalog.",
        );
      }
    })();
  }, [ar]);

  // Separate and non-blocking: an empty or failed "frequent" list is a missing
  // shortcut, not a broken screen, so it must never gate the catalog.
  useEffect(() => {
    apiFetch<{ items: { itemTypeId: string; serviceId: string }[] }>("/orders/frequent-combinations")
      .then((r) => setFrequent(r.items))
      .catch(() => setFrequent([]));
  }, []);

  // Only the auto-print preference is needed here. Failing to read it must not
  // block selling — a counter that cannot take money because a settings fetch
  // failed is a worse outcome than a receipt the operator prints by hand.
  const [invoiceSettings, setInvoiceSettings] = useState<{ autoPrintDirectSale: boolean } | null>(null);
  useEffect(() => {
    apiFetch<{ autoPrintDirectSale: boolean }>("/settings/invoice")
      .then(setInvoiceSettings)
      .catch(() => setInvoiceSettings(null));
  }, []);

  // ── Customer (optional) ──────────────────────────────────────────────────
  const [customerOpen, setCustomerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerHits, setCustomerHits] = useState<CustomerHit[]>([]);
  const [customer, setCustomer] = useState<CustomerHit | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (customer || customerSearch.trim().length < 2) {
      setCustomerHits([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await apiFetch<{ items: CustomerHit[] }>(
          `/customers?search=${encodeURIComponent(customerSearch.trim())}&pageSize=8`,
        );
        setCustomerHits(data.items);
      } catch {
        setCustomerHits([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [customerSearch, customer]);

  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerMobile, setNewCustomerMobile] = useState("");
  const [newCustomerError, setNewCustomerError] = useState<string | null>(null);
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  const createCustomerInline = async () => {
    setNewCustomerError(null);
    if (!newCustomerName.trim()) {
      setNewCustomerError(ar ? "الاسم مطلوب." : "A name is required.");
      return;
    }
    setCreatingCustomer(true);
    try {
      const created = await apiFetch<{ id: string }>("/customers", {
        method: "POST",
        body: { name: newCustomerName.trim(), mobile: newCustomerMobile.trim() || null },
      });
      setCustomer({
        id: created.id,
        name: newCustomerName.trim(),
        mobile: newCustomerMobile.trim() || null,
        outstandingMinor: 0,
      });
      setNewCustomerOpen(false);
      setNewCustomerName("");
      setNewCustomerMobile("");
      showToast("success", ar ? "تمت إضافة العميل." : "Customer added.");
    } catch (err) {
      setNewCustomerError(
        err instanceof ApiError && err.code === "mobile_already_registered"
          ? ar
            ? "رقم الجوال مسجّل لعميل آخر — ابحث عنه بدلاً من إنشائه."
            : "That mobile belongs to an existing customer — search for them instead."
          : err instanceof ApiError
            ? err.message
            : ar
              ? "تعذّر إضافة العميل."
              : "Couldn't add the customer.",
      );
    } finally {
      setCreatingCustomer(false);
    }
  };

  // ── Selection + lines ────────────────────────────────────────────────────
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [itemSearch, setItemSearch] = useState("");
  const [lines, setLines] = useState<OrderLine[]>([]);
  const [notes, setNotes] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Set briefly when a line's quantity changes, to pulse just that row. */
  const [bumpedKey, setBumpedKey] = useState<string | null>(null);
  const [method, setMethod] = useState<PaymentMethod>("CASH");
  const [completing, setCompleting] = useState(false);
  const [sale, setSale] = useState<DirectSaleResult | null>(null);
  /**
   * The idempotency key for the CURRENT basket.
   *
   * Minted once per sale attempt and reused across retries, so a double-click
   * or a retried request resolves to the same server-side transaction rather
   * than a second invoice. Cleared only when a new sale starts.
   */
  const idempotencyKey = useRef<string | null>(null);
  const servicePanelRef = useRef<HTMLDivElement | null>(null);

  const priceFor = useCallback(
    (serviceId: string, itemTypeId: string): number | null => {
      if (!prices || !serviceId || !itemTypeId) return null;
      const exact = prices.find((p) => p.serviceId === serviceId && p.itemTypeId === itemTypeId);
      if (exact) return exact.unitPriceMinor;
      const fallback = prices.find((p) => p.serviceId === serviceId && p.itemTypeId === null);
      return fallback ? fallback.unitPriceMinor : null;
    },
    [prices],
  );

  const itemById = useMemo(() => new Map((itemTypes ?? []).map((i) => [i.id, i])), [itemTypes]);
  const serviceById = useMemo(() => new Map((services ?? []).map((s) => [s.id, s])), [services]);

  const nameOf = useCallback(
    (entity: { nameAr: string; nameEn: string } | undefined) =>
      entity ? (ar ? entity.nameAr : entity.nameEn) : "",
    [ar],
  );

  const visibleItems = useMemo(() => {
    const term = itemSearch.trim().toLowerCase();
    if (!term) return itemTypes ?? [];
    return (itemTypes ?? []).filter(
      (i) => i.nameAr.toLowerCase().includes(term) || i.nameEn.toLowerCase().includes(term),
    );
  }, [itemTypes, itemSearch]);

  /**
   * Services offered for the selected item — only those that actually resolve
   * to a price. An unpriced pair cannot be ordered, so showing its tile would
   * be offering something the server will refuse.
   */
  const servicesForSelected = useMemo(() => {
    if (!selectedItemId || !services) return [];
    return services
      .map((s) => ({ service: s, unitPriceMinor: priceFor(s.id, selectedItemId) }))
      .filter((s): s is { service: CatalogService; unitPriceMinor: number } => s.unitPriceMinor !== null);
  }, [selectedItemId, services, priceFor]);

  /** Frequent pairs, intersected with the ACTIVE, PRICED catalog. */
  const frequentTiles = useMemo(() => {
    return frequent
      .map((f) => ({
        ...f,
        item: itemById.get(f.itemTypeId),
        service: serviceById.get(f.serviceId),
        unitPriceMinor: priceFor(f.serviceId, f.itemTypeId),
      }))
      .filter((f) => f.item && f.service && f.unitPriceMinor !== null)
      .slice(0, 6);
  }, [frequent, itemById, serviceById, priceFor]);

  const bump = (key: string) => {
    setBumpedKey(key);
    setTimeout(() => setBumpedKey((k) => (k === key ? null : k)), 220);
  };

  /** Adds the pair, or increments it when already on the order. */
  const addLine = (itemTypeId: string, serviceId: string) => {
    const mode = serviceById.get(serviceId)?.pricingMode ?? "per_item";
    setLines((prev) => {
      const existing = prev.find((l) => l.itemTypeId === itemTypeId && l.serviceId === serviceId);
      if (existing) {
        // A flat-rate service is charged once regardless of count, so a repeat
        // tap must not silently multiply it.
        if (mode === "flat") return prev;
        return prev.map((l) => (l === existing ? { ...l, quantity: l.quantity + 1 } : l));
      }
      return [...prev, { itemTypeId, serviceId, quantity: 1 }];
    });
    bump(lineKey(itemTypeId, serviceId));
  };

  const changeQuantity = (key: string, delta: number) => {
    setLines((prev) =>
      prev.flatMap((l) => {
        if (lineKey(l.itemTypeId, l.serviceId) !== key) return [l];
        const next = l.quantity + delta;
        return next <= 0 ? [] : [{ ...l, quantity: next }];
      }),
    );
    bump(key);
  };

  const removeLine = (key: string) =>
    setLines((prev) => prev.filter((l) => lineKey(l.itemTypeId, l.serviceId) !== key));

  // ── Totals (preview only — the server is authoritative) ──────────────────
  const priced = useMemo(
    () =>
      lines.map((line) => {
        const unitPriceMinor = priceFor(line.serviceId, line.itemTypeId);
        const lineTotalMinor =
          unitPriceMinor === null
            ? null
            : computeDocumentTax([
                {
                  unitPrice: unitPriceMinor,
                  quantity: line.quantity,
                  rateBps: tax.rateBps,
                  pricesInclusive: tax.pricesInclusive,
                },
              ]).totals.total;
        return { line, unitPriceMinor, lineTotalMinor };
      }),
    [lines, priceFor, tax],
  );

  const totals = useMemo(() => {
    const inputs: TaxLineInput[] = priced
      .filter((p) => p.unitPriceMinor !== null)
      .map((p) => ({
        unitPrice: p.unitPriceMinor!,
        quantity: p.line.quantity,
        rateBps: tax.rateBps,
        pricesInclusive: tax.pricesInclusive,
      }));
    return computeDocumentTax(inputs).totals;
  }, [priced, tax]);

  const totalPieces = lines.reduce((sum, l) => sum + l.quantity, 0);
  const canSave = lines.length > 0 && !saving;

  const save = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const created = await apiFetch<{ id: string; orderRef: string }>("/orders", {
        method: "POST",
        body: {
          // null is meaningful: an anonymous walk-in, not a missing field.
          customerId: customer?.id ?? null,
          items: lines.map((l) => ({
            itemTypeId: l.itemTypeId,
            serviceId: l.serviceId,
            quantity: l.quantity,
          })),
          notes: notes.trim() || null,
        },
      });
      showToast("success", ar ? `تم إنشاء الطلب ${created.orderRef}.` : `Order ${created.orderRef} created.`);
      navigate(`/orders/${created.id}`, { replace: true });
    } catch (err) {
      setSaveError(
        err instanceof ApiError && err.code === "price_not_found"
          ? ar
            ? "أحد الأصناف بلا سعر مُعتمد — راجع الأسعار."
            : "One of the combinations has no configured price."
          : err instanceof ApiError
            ? err.message
            : ar
              ? "تعذّر حفظ الطلب."
              : "Couldn't save the order.",
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Direct Sale — completed at the counter, paid in full.
   *
   * No amount is sent. The server prices the lines and posts a payment for
   * exactly its own total; `expectedTotalMinor` is sent only so the server can
   * REFUSE the sale if the price moved since this basket was built, rather
   * than silently charging something other than what was quoted.
   */
  const completeDirectSale = async () => {
    if (lines.length === 0) return;
    setSaveError(null);
    setCompleting(true);
    idempotencyKey.current ??= crypto.randomUUID();
    try {
      const result = await apiFetch<DirectSaleResult>("/orders/direct-sale", {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey.current },
        body: {
          customerId: customer?.id ?? null,
          items: lines.map((l) => ({
            itemTypeId: l.itemTypeId,
            serviceId: l.serviceId,
            quantity: l.quantity,
          })),
          method,
          notes: notes.trim() || null,
          expectedTotalMinor: totals.total,
        },
      });
      setSale(result);
      showToast("success", ar ? `تم البيع ${result.invoiceRef}.` : `Sale ${result.invoiceRef} completed.`);

      // AFTER the server confirmed the sale, never before. Auto-print is a
      // convenience layered on a committed transaction — printing first and
      // hoping would hand the customer a receipt for a sale that failed.
      if (invoiceSettings?.autoPrintDirectSale) {
        navigate(`/invoices/${result.invoiceId}?autoprint=1`);
      }
    } catch (err) {
      setSaveError(
        err instanceof ApiError && err.code === "total_mismatch"
          ? ar
            ? "تغيّر السعر منذ بدء العملية — راجع الإجمالي وأعد المحاولة."
            : "The price changed since this sale started — review the total and try again."
          : err instanceof ApiError
            ? err.message
            : ar
              ? "تعذّر إتمام البيع."
              : "Couldn't complete the sale.",
      );
    } finally {
      setCompleting(false);
    }
  };

  /** Clears the basket for the next customer. A NEW idempotency key is minted
   *  on the next completion, so this genuinely starts a separate transaction. */
  const startNewSale = () => {
    setSale(null);
    setLines([]);
    setCustomer(null);
    setNotes("");
    setSelectedItemId(null);
    setItemSearch("");
    setMethod("CASH");
    setSaveError(null);
    idempotencyKey.current = null;
  };

  const catalogLoading = itemTypes === null || services === null || prices === null;

  const selectItem = (id: string) => {
    setSelectedItemId((prev) => (prev === id ? null : id));
    // On a phone the service strip renders below the grid, which can sit under
    // the fold on a long catalogue.
    requestAnimationFrame(() => {
      if (window.matchMedia("(max-width: 1023px)").matches) {
        servicePanelRef.current?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "nearest",
        });
      }
    });
  };

  /* ── Order summary, shared by the desktop sidebar and the mobile panel ─── */
  const orderSummary = (
    <div className="space-y-3">
      {lines.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {ar ? "اختر صنفاً للبدء." : "Pick an item to start."}
        </p>
      ) : (
        <ul className="space-y-2">
          {priced.map(({ line, unitPriceMinor, lineTotalMinor }) => {
            const key = lineKey(line.itemTypeId, line.serviceId);
            const mode = serviceById.get(line.serviceId)?.pricingMode ?? "per_item";
            const unitDef = PRICING_MODE_DEFINITIONS[mode];
            return (
              <li
                key={key}
                className={`rounded-lg border border-line-hairline bg-card p-2.5 transition-transform duration-150 motion-reduce:transition-none ${
                  bumpedKey === key ? "scale-[1.015]" : "scale-100"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-ink-900">
                      {nameOf(itemById.get(line.itemTypeId))}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {nameOf(serviceById.get(line.serviceId))}
                      {unitPriceMinor !== null && (
                        <span className="tabular-nums">
                          {" · "}
                          {money(unitPriceMinor)}
                          {unitDef.unitEn !== "—" && `/${ar ? unitDef.unitAr : unitDef.unitEn}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-sm font-semibold tabular-nums text-ink-900">
                    {lineTotalMinor !== null ? money(lineTotalMinor) : "—"}
                  </div>
                </div>

                <div className="mt-2 flex items-center gap-1.5">
                  {mode === "flat" ? (
                    <span className="text-xs text-muted-foreground">
                      {ar ? "سعر ثابت" : "Flat rate"}
                    </span>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => changeQuantity(key, -1)}
                        aria-label={ar ? "إنقاص الكمية" : "Decrease quantity"}
                        className="flex size-8 items-center justify-center rounded-md border border-line text-ink-700 transition-colors hover:bg-ink-100 active:bg-ink-150"
                      >
                        <Minus className="size-3.5" aria-hidden="true" />
                      </button>
                      <span
                        aria-live="polite"
                        className="min-w-[2.25rem] text-center text-sm font-semibold tabular-nums text-ink-900"
                      >
                        {line.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => changeQuantity(key, 1)}
                        aria-label={ar ? "زيادة الكمية" : "Increase quantity"}
                        className="flex size-8 items-center justify-center rounded-md border border-line text-ink-700 transition-colors hover:bg-ink-100 active:bg-ink-150"
                      >
                        <Plus className="size-3.5" aria-hidden="true" />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => removeLine(key)}
                    aria-label={ar ? "حذف السطر" : "Remove line"}
                    className="ms-auto flex size-8 items-center justify-center rounded-md text-muted-subtle transition-colors hover:bg-danger-bg hover:text-danger"
                  >
                    <Trash2 className="size-3.5" aria-hidden="true" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* BarberPro's totals hierarchy: the working lines stay deliberately
          quiet (xs, muted) so the final figure carries all the weight at
          2xl/bold — the number the operator reads out to the customer. */}
      {lines.length > 0 && (
        <div className="space-y-1 border-t border-line-hairline pt-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{ar ? "قبل الضريبة" : "Subtotal"}</span>
            <span className="tabular-nums">
              {money(totals.net)} {currency}
            </span>
          </div>
          {tax.vatEnabled && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {ar ? "الضريبة" : "VAT"} ({formatRatePercent(tax.rateBps)})
              </span>
              <span className="tabular-nums">
                {money(totals.vat)} {currency}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between pt-1.5">
            <span className="text-sm font-medium text-ink-700">{ar ? "الإجمالي" : "Total"}</span>
            <span className="tabular-nums text-2xl font-bold tracking-tight text-ink-900">
              {money(totals.total)} {currency}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  return (
    // The fixed mobile bar floats over the page, so the last card needs its own
    // clearance or the Save button covers the notes field. Only while a bar
    // actually exists, and only below lg where the bar renders.
    <div className={`space-y-4 ${lines.length > 0 ? "pb-24 lg:pb-0" : ""}`}>
      <PageHeader
        title={ar ? "طلب جديد" : "New order"}
        subtitle={ar ? "استلام قطع — العميل اختياري" : "Take items in — customer optional"}
      />

      {/*
        Completion panel. A counter sale ends HERE, not on a detail page three
        navigations away — the operator needs the receipt and the next customer,
        in that order. Rendered above everything so it is the first thing seen.
      */}
      {sale && (
        <Card className="border-success-border bg-success-bg">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="flex size-7 items-center justify-center rounded-full bg-success text-white">
                  <Check className="size-4" aria-hidden="true" />
                </span>
                <span className="text-sm font-semibold text-success">
                  {ar ? "تم البيع ومدفوع بالكامل" : "Sale complete — paid in full"}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
                <span className="font-semibold tabular-nums text-ink-900">{sale.invoiceRef}</span>
                <span className="tabular-nums text-ink-700">
                  {money(sale.totalMinor)} {currency}
                </span>
                <span className="text-muted-foreground">
                  {ar
                    ? PAYMENT_METHODS.find((m) => m.value === sale.method)?.labelAr
                    : PAYMENT_METHODS.find((m) => m.value === sale.method)?.labelEn}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Print formats open the canonical invoice document — the same
                  renderer every other surface uses, never a POS-only copy. */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                icon={<Printer className="size-3.5" />}
                onClick={() => navigate(`/invoices/${sale.invoiceId}`)}
              >
                {ar ? "طباعة / PDF" : "Print / PDF"}
              </Button>
              <Button type="button" size="sm" onClick={startNewSale}>
                {ar ? "عملية جديدة" : "New sale"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {catalogError && (
        <Card>
          <ErrorText>{catalogError}</ErrorText>
        </Card>
      )}

      {/* ── Customer: compact, collapsed, never blocking ──────────────────── */}
      <Card padding="sm">
        {customer ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-ink-900">{customer.name}</div>
              {customer.mobile && (
                <div className="tabular-nums text-xs text-muted-foreground">{customer.mobile}</div>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon={<X className="size-3.5" />}
              onClick={() => {
                setCustomer(null);
                setCustomerSearch("");
              }}
            >
              {ar ? "إزالة" : "Remove"}
            </Button>
          </div>
        ) : customerOpen ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[180px] flex-1">
                <Search
                  className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-subtle"
                  aria-hidden="true"
                />
                <input
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder={ar ? "ابحث بالاسم أو الجوال" : "Search by name or mobile"}
                  autoFocus
                  className="h-11 w-full rounded-lg border border-line-strong bg-card ps-9 pe-3 text-sm text-ink-900 outline-none transition-colors focus:border-accent-500"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                icon={<UserPlus className="size-3.5" />}
                onClick={() => setNewCustomerOpen(true)}
                className="h-11"
              >
                {ar ? "عميل جديد" : "New"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCustomerOpen(false);
                  setCustomerSearch("");
                }}
                className="h-11"
              >
                {ar ? "إلغاء" : "Cancel"}
              </Button>
            </div>

            {searching && <Skeleton className="h-9 w-full" />}

            {customerHits.length > 0 && (
              <ul className="overflow-hidden rounded-lg border border-line">
                {customerHits.map((hit) => (
                  <li key={hit.id} className="border-b border-line-hairline last:border-0">
                    <button
                      type="button"
                      onClick={() => {
                        setCustomer(hit);
                        setCustomerSearch("");
                        setCustomerOpen(false);
                      }}
                      className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-start transition-colors hover:bg-ink-50"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ink-900">{hit.name}</span>
                        {hit.mobile && (
                          <span className="tabular-nums text-xs text-muted-foreground">{hit.mobile}</span>
                        )}
                      </span>
                      {hit.outstandingMinor > 0 && (
                        <span className="shrink-0 text-xs font-medium tabular-nums text-warning">
                          {money(hit.outstandingMinor)} {currency}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {!searching && customerSearch.trim().length >= 2 && customerHits.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {ar ? "لا نتائج — أضف عميلاً جديداً." : "No matches — add a new customer."}
              </p>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCustomerOpen(true)}
            // min-h-11 because the row's own box — not the card padding around
            // it — is what the browser hit-tests. At its natural 20px it was a
            // thumb-sized miss on a counter tablet.
            className="flex min-h-11 w-full items-center justify-between gap-3 text-start"
          >
            <span className="text-sm text-muted-foreground">
              {ar ? "العميل — اختياري" : "Customer — optional"}
            </span>
            <span className="flex items-center gap-1.5 text-sm font-medium text-accent-600">
              {ar ? "إضافة بيانات العميل" : "Add customer"}
              <ChevronDown className="size-4" aria-hidden="true" />
            </span>
          </button>
        )}
      </Card>

      {/* 2:1 split, matching BarberPro's POS grid (`lg:grid-cols-3` with the
          catalog spanning two columns). In RTL the shell's sidebar is on the
          right, so the catalog reads first and the order panel lands on the
          left — the operator terminal arrangement the directive specifies. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-start">
        {/* ── Catalog ───────────────────────────────────────────────────── */}
        <div className="space-y-4 lg:col-span-2">
          {catalogLoading ? (
            <Card>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            </Card>
          ) : (
            <>
              {frequentTiles.length > 0 && (
                <Card padding="sm">
                  <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-subtle">
                    {ar ? "الأكثر استخداماً" : "Most used"}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {frequentTiles.map((f) => (
                      <button
                        key={`${f.itemTypeId}-${f.serviceId}`}
                        type="button"
                        onClick={() => addLine(f.itemTypeId, f.serviceId)}
                        className="group flex items-center gap-2 rounded-lg border border-line bg-card px-3 py-2 text-start transition-all duration-150 hover:border-line-strong hover:bg-ink-50 active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100"
                      >
                        <span className="text-sm font-medium text-ink-900">{nameOf(f.item)}</span>
                        <span className="text-xs text-muted-foreground">{nameOf(f.service)}</span>
                        <span className="text-xs font-semibold tabular-nums text-accent-600">
                          {money(f.unitPriceMinor!)}
                        </span>
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              <Card padding="sm">
                {/* Search only earns its space once the catalogue is long
                    enough to scan rather than see. */}
                {(itemTypes ?? []).length > 8 && (
                  <div className="relative mb-3">
                    <Search
                      className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-subtle"
                      aria-hidden="true"
                    />
                    <input
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                      placeholder={ar ? "ابحث عن صنف" : "Search items"}
                      aria-label={ar ? "ابحث عن صنف" : "Search items"}
                      className="h-11 w-full rounded-lg border border-line-strong bg-card ps-9 pe-3 text-sm text-ink-900 outline-none transition-colors focus:border-accent-500"
                    />
                  </div>
                )}

                {visibleItems.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    {itemSearch
                      ? ar
                        ? "لا نتائج."
                        : "No matches."
                      : ar
                        ? "لا توجد أصناف — أضفها من الأصناف والخدمات."
                        : "No item types yet — add them under Services."}
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                    {visibleItems.map((item) => {
                      const selected = selectedItemId === item.id;
                      const count = lines
                        .filter((l) => l.itemTypeId === item.id)
                        .reduce((s, l) => s + l.quantity, 0);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => selectItem(item.id)}
                          aria-pressed={selected}
                          // BarberPro's POS tile treatment: a soft lift on hover
                          // (`-translate-y-0.5` + shadow step) and a press that
                          // settles back. It is what makes a tile grid read as an
                          // operator terminal rather than a list of checkboxes.
                          className={`group relative flex min-h-[4.5rem] flex-col items-center justify-center gap-1 rounded-xl border p-3.5 text-center shadow-sm transition-all duration-150 ease-out active:translate-y-0 active:scale-[0.98] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100 ${
                            selected
                              ? "border-accent-500 bg-accent-50 shadow-accent"
                              : "border-line/80 bg-card hover:-translate-y-0.5 hover:border-accent-300 hover:shadow-md"
                          }`}
                        >
                          <span
                            className={`text-sm font-medium leading-snug ${
                              selected ? "text-accent-700" : "text-ink-900"
                            }`}
                          >
                            {nameOf(item)}
                          </span>
                          {count > 0 && (
                            <span className="absolute end-1.5 top-1.5 flex min-w-[1.25rem] items-center justify-center rounded-full bg-ink-900 px-1 text-[11px] font-semibold tabular-nums text-white">
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </Card>

              {/* ── Services for the selected item ───────────────────────── */}
              <div ref={servicePanelRef}>
                {selectedItemId && (
                  <Card padding="sm">
                    <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-subtle">
                      {ar ? "الخدمة لـ " : "Service for "}
                      <span className="text-ink-700">{nameOf(itemById.get(selectedItemId))}</span>
                    </h2>

                    {servicesForSelected.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">
                        {ar
                          ? "لا توجد خدمة مُسعّرة لهذا الصنف."
                          : "No priced service for this item yet."}
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {servicesForSelected.map(({ service, unitPriceMinor }) => {
                          const key = lineKey(selectedItemId, service.id);
                          const onOrder = lines.find(
                            (l) => l.itemTypeId === selectedItemId && l.serviceId === service.id,
                          );
                          return (
                            <button
                              key={service.id}
                              type="button"
                              onClick={() => addLine(selectedItemId, service.id)}
                              className={`flex min-h-[4rem] flex-col items-start justify-center gap-0.5 rounded-xl border p-3.5 text-start shadow-sm transition-all duration-150 ease-out active:translate-y-0 active:scale-[0.98] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100 ${
                                onOrder
                                  ? "border-accent-500 bg-accent-50"
                                  : "border-line/80 bg-card hover:-translate-y-0.5 hover:border-accent-300 hover:shadow-md"
                              }`}
                            >
                              <span className="flex w-full items-center gap-1.5">
                                <span
                                  className={`truncate text-sm font-medium ${
                                    onOrder ? "text-accent-700" : "text-ink-900"
                                  }`}
                                >
                                  {nameOf(service)}
                                </span>
                                {onOrder && (
                                  <Check className="ms-auto size-3.5 shrink-0 text-accent-600" aria-hidden="true" />
                                )}
                              </span>
                              <span className="text-xs font-semibold tabular-nums text-muted-foreground">
                                {money(unitPriceMinor)} {currency}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Order summary: sticky sidebar on desktop, in flow on mobile ── */}
        <Card padding="sm" className="lg:sticky lg:top-4">
          <h2 className="mb-3 flex items-center justify-between gap-2 text-sm font-semibold text-ink-900">
            {ar ? "الطلب الحالي" : "Current order"}
            {/* BarberPro's near-black count pill: the one piece of high-contrast
                emphasis in the panel header, so piece count is readable at a
                glance across a counter. */}
            {totalPieces > 0 && (
              <span className="rounded-full bg-ink-900 px-2 py-0.5 text-[11px] font-medium tabular-nums text-white">
                {totalPieces} {ar ? "قطعة" : totalPieces === 1 ? "piece" : "pieces"}
              </span>
            )}
          </h2>

          {orderSummary}

          <div className="mt-3">
            <Input
              label={ar ? "ملاحظات" : "Notes"}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* ── Payment method — Direct Sale only ───────────────────────── */}
          {lines.length > 0 && (
            <div className="mt-3">
              <div className="mb-1.5 text-xs font-medium text-ink-600">
                {ar ? "طريقة الدفع" : "Payment method"}
              </div>
              <div role="group" aria-label={ar ? "طريقة الدفع" : "Payment method"} className="grid grid-cols-4 gap-1.5">
                {PAYMENT_METHODS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={method === opt.value}
                    onClick={() => setMethod(opt.value)}
                    className={`flex flex-col items-center gap-1 rounded-lg border py-2.5 text-[11px] font-medium transition-all duration-150 active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 ${
                      method === opt.value
                        ? "border-accent-400 bg-accent-50 text-accent-600 shadow-xs ring-1 ring-inset ring-accent-200"
                        : "border-line text-muted-foreground hover:border-line-strong hover:bg-ink-50"
                    }`}
                  >
                    <opt.icon className="size-4" aria-hidden="true" />
                    {ar ? opt.labelAr : opt.labelEn}
                  </button>
                ))}
              </div>
            </div>
          )}

          <ErrorText>{saveError}</ErrorText>

          {/*
            TWO DISTINCT OUTCOMES, and the hierarchy has to say which is which.

            "إتمام البيع" is the counter transaction: paid in full, invoiced,
            done — so it is the primary action. "استلام طلب" hands garments to
            the laundry pipeline and is settled later; it is secondary, not
            lesser. Making both look equal is how an operator takes payment for
            an order that was meant to be processed, or vice versa.
          */}
          <div className="mt-3 hidden flex-col gap-2 lg:flex">
            <Button
              type="button"
              size="lg"
              loading={completing}
              disabled={lines.length === 0 || completing || saving}
              onClick={completeDirectSale}
            >
              {ar ? "إتمام البيع" : "Complete sale"}
            </Button>
            <Button
              type="button"
              variant="outline"
              loading={saving}
              disabled={!canSave || completing}
              onClick={save}
            >
              {ar ? "استلام طلب" : "Take in as order"}
            </Button>
          </div>
        </Card>
      </div>

      {/*
        Mobile action bar — FIXED, not sticky.
        `position: sticky` was the obvious choice and is wrong here: a sticky
        element can only float within its own parent's box, and this bar is the
        last child of the page container, so its parent ends exactly where the
        bar does and it never lifts. Measured on the deployed build at 375×812
        it sat at y=946 — permanently below the fold, which is the opposite of
        an always-reachable total. Fixed, offset by the tab bar's height plus
        the safe-area inset, puts it just above the bottom navigation.
      */}
      {lines.length > 0 && (
        <div className="fixed inset-x-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-20 animate-slide-up lg:hidden motion-reduce:animate-none">
          <div className="flex items-center gap-3 rounded-xl bg-ink-900 p-3 text-white shadow-lg">
            <div className="min-w-0">
              {/* On the near-black bar these invert: champagne for the count,
                  white for the figure — BarberPro's floating cart treatment. */}
              <div className="text-[11px] text-accent-300">
                {totalPieces} {ar ? "قطعة" : totalPieces === 1 ? "piece" : "pieces"}
              </div>
              <div className="truncate text-base font-bold tabular-nums text-white">
                {money(totals.total)} {currency}
              </div>
            </div>
            <Button
              type="button"
              size="lg"
              loading={completing}
              disabled={lines.length === 0 || completing || saving}
              onClick={completeDirectSale}
              className="ms-auto flex-1"
            >
              {ar ? "إتمام البيع" : "Complete sale"}
            </Button>
          </div>
        </div>
      )}

      <Modal
        open={newCustomerOpen}
        onClose={() => setNewCustomerOpen(false)}
        title={ar ? "عميل جديد" : "New customer"}
        closeLabel={ar ? "إغلاق" : "Close"}
      >
        <div className="space-y-3">
          <Input
            label={ar ? "الاسم" : "Name"}
            value={newCustomerName}
            onChange={(e) => setNewCustomerName(e.target.value)}
            required
            autoFocus
          />
          <Input
            label={ar ? "الجوال" : "Mobile"}
            value={newCustomerMobile}
            onChange={(e) => setNewCustomerMobile(e.target.value)}
            placeholder="05xxxxxxxx"
            inputMode="tel"
          />
          <ErrorText>{newCustomerError}</ErrorText>
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setNewCustomerOpen(false)}>
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button type="button" loading={creatingCustomer} onClick={createCustomerInline}>
              {ar ? "إضافة" : "Add"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
